import { z } from 'zod'

import {
  currencyGroupingSchema,
  isoInstantSchema,
  phoneSchema,
  positivePaisaSchema,
  sessionTransportSchema,
  slugSchema,
  usernameSchema,
} from '../common'
import { meResponse } from './me'

/**
 * A1–A7 · signup, sign-in and passwords.
 *
 * Two rules run through all of it. Usernames are unique **per tenant** (§3.2),
 * so `username + password` is ambiguous and every tenant sign-in carries a
 * `tenantSlug` (§4.20). And every failure is the same failure: unknown
 * business, unknown user and wrong password all return `INVALID_CREDENTIALS`
 * with no distinction, because distinguishing them enumerates accounts.
 */

/** Bounded so a password manager's paste is fine and a 1MB body is not. */
export const passwordSchema = z.string().min(8).max(128)

/** OTP: 6 digits, 10-minute expiry, 5 attempts (§3.1 `otp_verifications`). */
export const otpCodeSchema = z.string().regex(/^\d{6}$/, 'The code is 6 digits.')

/* ── A1 · POST /auth/signup/start ─────────────────────────────────────────── */

export const signupStartRequest = z.object({ phone: phoneSchema })

export const otpChallengeResponse = z.object({
  phone: phoneSchema,
  expiresAt: isoInstantSchema,
  /** Seconds before "Resend" may be pressed. Rate-limited per IP and phone. */
  resendAfterSeconds: z.number().int().nonnegative(),
  attemptsRemaining: z.number().int().nonnegative(),
})

/* ── A2 · POST /auth/signup/verify ────────────────────────────────────────── */

export const signupVerifyRequest = z.object({ phone: phoneSchema, code: otpCodeSchema })

export const signupVerifyResponse = z.object({
  /** Bearer for A3 only, 15 minutes. Not a session: it can create one book. */
  signupToken: z.string().min(1),
  expiresAt: isoInstantSchema,
})

/* ── A3 · POST /auth/signup/complete ──────────────────────────────────────── */

/**
 * Creates the tenant, its owner, a `TRIAL` subscription, the default feed plan
 * and `tenant_settings` in **one transaction**. `409` on a taken username or
 * slug. The daily rate is asked for here because a book with no feed rate
 * silently under-costs every animal from day one.
 */
export const signupCompleteRequest = z.object({
  businessName: z.string().trim().min(2).max(120),
  ownerName: z.string().trim().min(2).max(120),
  username: usernameSchema,
  password: passwordSchema,
  dailyRatePaisa: positivePaisaSchema,
  currencyGrouping: currencyGroupingSchema.default('indian'),
})

/* ── A4 · POST /auth/login ────────────────────────────────────────────────── */

export const loginRequest = z.object({
  tenantSlug: slugSchema,
  username: usernameSchema,
  password: passwordSchema,
})

/**
 * The session plus the whole `/me` payload, so the app renders its first screen
 * without a second round trip on a mandi connection.
 *
 * `token` is present only for `BEARER` transport (mobile). On the web the
 * session arrives as an `httpOnly` cookie and the key is absent — a token in
 * a JSON body that JavaScript can read is the thing `httpOnly` exists to
 * prevent.
 */
export const sessionResponse = z.object({
  token: z.string().min(1).optional(),
  transport: sessionTransportSchema,
  expiresAt: isoInstantSchema,
  csrfToken: z.string().min(1).optional(),
  me: meResponse,
})

/* ── A5 · POST /auth/logout · POST /auth/refresh ──────────────────────────── */

export const logoutResponse = z.object({ loggedOut: z.literal(true) })

/**
 * A8b · `POST /auth/logout/all` — end every session this user has, this one
 * included. For the phone left at the mandi: sign in on another and put the
 * lost one out. `sessionsEnded` is said back so the app can say it out loud.
 */
export const logoutAllResponse = z.object({
  loggedOut: z.literal(true),
  sessionsEnded: z.number().int().nonnegative(),
})

export type LogoutAllResponse = z.infer<typeof logoutAllResponse>

export const refreshResponse = z.object({
  token: z.string().min(1).optional(),
  expiresAt: isoInstantSchema,
})

/* ── A6 · POST /auth/password/forgot · /reset ─────────────────────────────── */

export const passwordForgotRequest = z.object({
  tenantSlug: slugSchema,
  username: usernameSchema,
})

export const passwordResetRequest = z.object({
  tenantSlug: slugSchema,
  username: usernameSchema,
  code: otpCodeSchema,
  newPassword: passwordSchema,
})

/**
 * A6/A7 both destroy **every other session** on success (SOW §7.5, defect F8):
 * a password changed because it leaked is worthless while the leaked session
 * is still live. The count is returned so the UI can say so out loud.
 */
export const passwordChangedResponse = z.object({
  changed: z.literal(true),
  otherSessionsRevoked: z.number().int().nonnegative(),
})

/* ── A7 · POST /auth/password/change ──────────────────────────────────────── */

export const passwordChangeRequest = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'Choose a different password.',
    path: ['newPassword'],
  })

export type SignupStartRequest = z.input<typeof signupStartRequest>
export type SignupVerifyRequest = z.input<typeof signupVerifyRequest>
export type SignupVerifyResponse = z.infer<typeof signupVerifyResponse>
export type SignupCompleteRequest = z.input<typeof signupCompleteRequest>
export type LoginRequest = z.input<typeof loginRequest>
export type SessionResponse = z.infer<typeof sessionResponse>
export type RefreshResponse = z.infer<typeof refreshResponse>
export type OtpChallengeResponse = z.infer<typeof otpChallengeResponse>
export type PasswordForgotRequest = z.input<typeof passwordForgotRequest>
export type PasswordResetRequest = z.input<typeof passwordResetRequest>
export type PasswordChangeRequest = z.input<typeof passwordChangeRequest>
export type PasswordChangedResponse = z.infer<typeof passwordChangedResponse>
